import {Component} from "@angular/core";
import {DialogService} from "../../bootstrap-modal/dialog.service";
import {DialogComponent} from "../../bootstrap-modal/dialog.component";
import "rxjs/add/operator/toPromise";
import {Observable} from "rxjs";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {toggleLoading} from "../../misc/loading.decorator";
import {SearchHelpers} from "../../helpers/search-helper";
import {FleetCreateDialogComponent} from "./fleet-create-dialog.component";
import * as uuid from "uuid";
import { FleetModel, fleetModelFactory } from "../../global-deal-settings/global-deal-settings.service";

export interface SelectDialogFleetModel extends FleetModel{
    selected: boolean;
    disabled: boolean;
    uniqueId: string;
}

export interface SelectFleetDialogModel {
  fleet: SelectDialogFleetModel[];
  disableAddNewFleetButton: boolean;
  projectFleetId : string;
  projectFleetName : string;
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 5;

@Component({
  templateUrl: "./fleet-select-dialog.template.pug",
})
export class FleetSelectDialogComponent
  extends DialogComponent<SelectFleetDialogModel, any> implements SelectFleetDialogModel {
  fleet: SelectDialogFleetModel[] = [];
  fleetFiltered: SelectDialogFleetModel[] = [];
  fleetToShow: SelectDialogFleetModel[] = [];
  selectedFleetUniqueId: string = "";
  disableAddNewFleetButton: boolean;
  public newFleets: FleetModel[] = [];
  public error: Error;
  loading: boolean = true;
  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number = 1;
  searchText: string = "";
  projectFleetName: string = "";
  projectFleetId: string = "";

  static showSelectFleetDialogModal(
    dialogService: DialogService,
    inputs: SelectFleetDialogModel,
  ): Observable<any> {
    return dialogService.addDialog(
      FleetSelectDialogComponent,
      inputs,
      {backdropColor: "rgba(0, 0, 0, 0.5)"},
    );
  }

  cancel(): void {
    this.result = {
      selectedFleet: null,
      newFleets: null,
    };
    this.close();
  }

  @handleErrors()
  async save(): Promise<void> {
    this.result = {
      selectedFleet: fleetModelFactory(
        this.fleet.find(fleet => fleet.uniqueId === this.selectedFleetUniqueId),
      ),
      newFleets: this.newFleets,
    };
    this.close();
  }

  @toggleLoading()
  @handleErrors()
  async ready() {
    const selectedFleet = this.fleet.find(fleet => !!fleet.selected);
    this.selectedFleetUniqueId = selectedFleet ? selectedFleet.uniqueId : "";
    // Initial Load the PO's to the Grid List
    this.loadFilteredFleet();
    this.disableAddNewFleetButton = this.disableAddNewFleetButton;
  }


  private loadFilteredFleet(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.fleetFiltered = this.fleet.filter((fleet) => {
      if (this.searchText) {
        const values = [
          fleet.fleetId,
          fleet.fleetName,
        ].filter(it => !!it);

        const searchFailures = SearchHelpers.tokenizeString(this.searchText) // split on spaces but consider "" and ''
          .map(v => SearchHelpers.stripQuotes(v)) // strip quotes on ends if present
          .map(v => SearchHelpers.cleanParam(v)) // sanitize to create regexp
          .map(v => new RegExp(v, "i")) // create regexp
          .map(re => values.some(v => re.test(v))) // test regexp against all asset values
          .filter(it => it === false) // don't care about success
          .length;

        if (searchFailures) return false;
      }
      return true;
    });
    this.fleetToShow = [
      ...this.fleetFiltered.filter(it => it.uniqueId === this.selectedFleetUniqueId),
      ...(this.fleetFiltered.slice(start, end).filter(
        it => it.uniqueId !== this.selectedFleetUniqueId, // Prevent duplicates in detail display
      ))];
    this.maxPages = Math.ceil(this.fleetFiltered.length / (+this.pageSize));
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }
    this.loadFilteredFleet();
  }

  async addNewFleet() {
    if (this.disableAddNewFleetButton) return;
    await FleetCreateDialogComponent.showCreateFleetDialogModal(
      this.dialogService,
      {fleet: this.fleet, fleetId: uuid.v1().replace(/-/g, ""), projectFleetId: this.projectFleetId, projectFleetName: this.projectFleetName},
    ).subscribe((it) => {
      if (it.fleetId && it.fleetName) {
        const newFleet = fleetModelFactory({
          fleetId: it.fleetId,
          fleetName: it.fleetName,
        });
        const uniqueId = uuid.v1();
        // Add the new fleet into the list of fleets
        this.fleet.push({...newFleet, selected: true, disabled: false, uniqueId});
        // Add the new fleet into the new fleets List
        this.newFleets.push(newFleet);
        // Mark the new fleet as selected
        this.selectedFleetUniqueId = uniqueId;
        // Disable the Add new fleet Button (we just allowed one new)
        this.disableAddNewFleetButton = true;
        // Update the Grid
        this.loadFilteredFleet();
      }
    });
  }

  changeFleetSelection(fleetUniqueId): void {
    this.selectedFleetUniqueId = fleetUniqueId;
    this.loadFilteredFleet();
  }

  constructor(
    public dialogService: DialogService,
  ) {
    super(dialogService);
  }
}
